import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BarChart3, Search, Loader2, X, DollarSign,
  TrendingUp, TrendingDown, AlertTriangle, Package, ChevronDown,
  Percent, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;
type Category = Tables<"categories">;

interface IngredientLink {
  ingredient_id: string;
  ingredient_name: string;
  quantity_used: number;
  unit: string;
  cost_per_unit: number;
  cost_total: number;
}

interface ProductCost {
  product_id: string;
  product_name: string;
  category_name: string | null;
  price: number;
  image_url: string | null;
  ingredients: IngredientLink[];
  total_cost: number;
  margin: number;
  margin_pct: number;
}

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const formatPct = (v: number) => `${v.toFixed(1)}%`;

const RelatorioCustos = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [productCosts, setProductCosts] = useState<ProductCost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"name" | "cost" | "margin" | "margin_pct">("margin_pct");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductCost | null>(null);

  const fetchData = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }
    
    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
      rest = data;
    }
    
    if (!rest) { navigate("/dashboard"); return; }

    const [{ data: prods }, { data: cats }, { data: ings }, { data: pIngs }] = await Promise.all([
      supabase.from("products").select("*").eq("restaurant_id", rest.id),
      supabase.from("categories").select("*").eq("restaurant_id", rest.id),
      supabase.from("ingredients").select("*").eq("restaurant_id", rest.id),
      supabase.from("product_ingredients").select("*"),
    ]);

    const allProducts = prods || [];
    const allCategories = cats || [];
    const allIngredients = (ings || []) as any[];
    const allPIngredients = (pIngs || []) as any[];

    setCategories(allCategories);

    const costs: ProductCost[] = allProducts.map((p) => {
      const cat = allCategories.find((c) => c.id === p.category_id);
      const links = allPIngredients
        .filter((pi: any) => pi.product_id === p.id)
        .map((pi: any) => {
          const ing = allIngredients.find((i: any) => i.id === pi.ingredient_id);
          if (!ing) return null;
          const costTotal = Number(pi.quantity_used) * Number(ing.cost_per_unit);
          return {
            ingredient_id: ing.id,
            ingredient_name: ing.name,
            quantity_used: Number(pi.quantity_used),
            unit: ing.unit,
            cost_per_unit: Number(ing.cost_per_unit),
            cost_total: costTotal,
          };
        })
        .filter(Boolean) as IngredientLink[];

      const totalCost = links.reduce((s, l) => s + l.cost_total, 0);
      const price = Number(p.price);
      const margin = price - totalCost;
      const marginPct = price > 0 ? (margin / price) * 100 : 0;

      return {
        product_id: p.id,
        product_name: p.name,
        category_name: cat?.name || null,
        price,
        image_url: p.image_url,
        ingredients: links,
        total_cost: totalCost,
        margin,
        margin_pct: marginPct,
      };
    });

    setProductCosts(costs);
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let result = [...productCosts];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) =>
        p.product_name.toLowerCase().includes(q) ||
        p.ingredients.some((i) => i.ingredient_name.toLowerCase().includes(q))
      );
    }

    if (categoryFilter !== "all") {
      result = result.filter((p) => p.category_name === categoryFilter);
    }

    switch (sortBy) {
      case "name": result.sort((a, b) => a.product_name.localeCompare(b.product_name)); break;
      case "cost": result.sort((a, b) => b.total_cost - a.total_cost); break;
      case "margin": result.sort((a, b) => a.margin - b.margin); break;
      case "margin_pct": result.sort((a, b) => a.margin_pct - b.margin_pct); break;
    }

    return result;
  }, [productCosts, search, categoryFilter, sortBy]);

  const stats = useMemo(() => {
    const withIngredients = productCosts.filter((p) => p.ingredients.length > 0);
    const avgMargin = withIngredients.length > 0
      ? withIngredients.reduce((s, p) => s + p.margin_pct, 0) / withIngredients.length
      : 0;
    const lowMargin = withIngredients.filter((p) => p.margin_pct < 30).length;
    const avgCost = withIngredients.length > 0
      ? withIngredients.reduce((s, p) => s + p.total_cost, 0) / withIngredients.length
      : 0;
    return {
      total: productCosts.length,
      withIngredients: withIngredients.length,
      avgMargin,
      lowMargin,
      avgCost,
    };
  }, [productCosts]);

  const getMarginColor = (pct: number) => {
    if (pct >= 60) return "text-success";
    if (pct >= 30) return "text-warning";
    return "text-destructive";
  };

  const getMarginBadge = (pct: number) => {
    if (pct >= 60) return { label: "Excelente", color: "bg-success/10 text-success border-success/30" };
    if (pct >= 30) return { label: "Boa", color: "bg-warning/10 text-warning border-warning/30" };
    return { label: "Baixa", color: "bg-destructive/10 text-destructive border-destructive/30" };
  };

  const openDetail = (p: ProductCost) => {
    setSelectedProduct(p);
    setDetailOpen(true);
  };

  const uniqueCategories = useMemo(() => {
    return [...new Set(productCosts.map((p) => p.category_name).filter(Boolean))].sort() as string[];
  }, [productCosts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/estoque")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <BarChart3 className="h-5 w-5 text-primary" />
            <h1 className="font-display font-bold text-lg">Custo de Produção</h1>
            <Badge variant="secondary" className="text-xs">{filtered.length} produtos</Badge>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Produtos com custo", value: `${stats.withIngredients}/${stats.total}`, icon: Package, accent: "text-foreground" },
            { label: "Margem Média", value: formatPct(stats.avgMargin), icon: TrendingUp, accent: getMarginColor(stats.avgMargin) },
            { label: "Custo Médio", value: formatCurrency(stats.avgCost), icon: DollarSign, accent: "text-primary" },
            { label: "Margem Baixa (<30%)", value: String(stats.lowMargin), icon: AlertTriangle, accent: stats.lowMargin > 0 ? "text-destructive" : "text-success" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-gradient-card shadow-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`h-4 w-4 ${s.accent}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className={`text-xl font-bold font-display ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar produto ou ingrediente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-card border-border" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {uniqueCategories.length > 0 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[160px] h-10 bg-card border-border">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas categorias</SelectItem>
                {uniqueCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
            <SelectTrigger className="w-[170px] h-10 bg-card border-border">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="margin_pct">Menor margem %</SelectItem>
              <SelectItem value="margin">Menor margem R$</SelectItem>
              <SelectItem value="cost">Maior custo</SelectItem>
              <SelectItem value="name">Nome A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Products list */}
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-border bg-gradient-card shadow-card p-12 text-center">
            <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">Nenhum produto encontrado</p>
            <p className="text-xs text-muted-foreground mt-1">Vincule ingredientes aos produtos na página de Estoque</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((p) => {
              const badge = p.ingredients.length > 0 ? getMarginBadge(p.margin_pct) : null;
              return (
                <motion.div
                  key={p.product_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-border bg-gradient-card shadow-card p-4 hover:bg-secondary/20 transition-colors cursor-pointer"
                  onClick={() => openDetail(p)}
                >
                  <div className="flex items-center gap-4">
                    {/* Product info */}
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        <Package className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium truncate">{p.product_name}</p>
                        {p.category_name && (
                          <Badge variant="secondary" className="text-[10px] shrink-0">{p.category_name}</Badge>
                        )}
                      </div>

                      {p.ingredients.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>Preço: <strong className="text-foreground">{formatCurrency(p.price)}</strong></span>
                          <span>Custo: <strong className="text-foreground">{formatCurrency(p.total_cost)}</strong></span>
                          <span>Margem: <strong className={getMarginColor(p.margin_pct)}>{formatCurrency(p.margin)} ({formatPct(p.margin_pct)})</strong></span>
                          <span className="text-[11px]">{p.ingredients.length} ingrediente{p.ingredients.length !== 1 ? "s" : ""}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Sem ingredientes vinculados — <span className="underline" onClick={(e) => { e.stopPropagation(); navigate("/estoque"); }}>vincular no estoque</span></p>
                      )}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {badge && (
                        <Badge variant="outline" className={`text-[10px] ${badge.color}`}>{badge.label}</Badge>
                      )}

                      {p.ingredients.length > 0 && (
                        <div className="hidden sm:block w-24">
                          <Progress
                            value={Math.min(100, p.margin_pct)}
                            className="h-2"
                          />
                        </div>
                      )}

                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  {selectedProduct.image_url ? (
                    <img src={selectedProduct.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p>{selectedProduct.product_name}</p>
                    {selectedProduct.category_name && (
                      <p className="text-xs text-muted-foreground font-normal">{selectedProduct.category_name}</p>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className="text-lg font-bold font-display">{formatCurrency(selectedProduct.price)}</p>
                  <p className="text-[11px] text-muted-foreground">Preço de Venda</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className="text-lg font-bold font-display text-destructive">{formatCurrency(selectedProduct.total_cost)}</p>
                  <p className="text-[11px] text-muted-foreground">Custo Total</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className={`text-lg font-bold font-display ${getMarginColor(selectedProduct.margin_pct)}`}>
                    {formatPct(selectedProduct.margin_pct)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Margem</p>
                </div>
              </div>

              {/* Margin bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Custo ({formatPct(100 - selectedProduct.margin_pct)})</span>
                  <span>Margem ({formatPct(selectedProduct.margin_pct)})</span>
                </div>
                <div className="w-full h-4 rounded-full bg-secondary overflow-hidden flex">
                  <div
                    className="h-full bg-destructive/60 transition-all"
                    style={{ width: `${Math.min(100, 100 - selectedProduct.margin_pct)}%` }}
                  />
                  <div
                    className="h-full bg-success/60 transition-all"
                    style={{ width: `${Math.min(100, selectedProduct.margin_pct)}%` }}
                  />
                </div>
              </div>

              {/* Ingredients breakdown */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <DollarSign className="h-4 w-4 text-primary" />
                  Composição de Custo
                </h3>
                {selectedProduct.ingredients.length === 0 ? (
                  <div className="text-center py-6 border border-dashed border-border rounded-lg">
                    <p className="text-sm text-muted-foreground">Sem ingredientes vinculados</p>
                    <Button variant="link" size="sm" onClick={() => navigate("/estoque")} className="text-primary mt-1">
                      Vincular ingredientes
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedProduct.ingredients
                      .sort((a, b) => b.cost_total - a.cost_total)
                      .map((ing) => {
                        const pct = selectedProduct.total_cost > 0
                          ? (ing.cost_total / selectedProduct.total_cost) * 100
                          : 0;
                        return (
                          <div key={ing.ingredient_id} className="rounded-lg border border-border p-3">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium">{ing.ingredient_name}</p>
                              <span className="text-sm font-bold">{formatCurrency(ing.cost_total)}</span>
                            </div>
                            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-2">
                              <span>{ing.quantity_used} {ing.unit} × {formatCurrency(ing.cost_per_unit)}/{ing.unit}</span>
                              <span>{formatPct(pct)} do custo</span>
                            </div>
                            <Progress value={pct} className="h-1.5" />
                          </div>
                        );
                      })}

                    {/* Total */}
                    <div className="rounded-lg bg-secondary/50 p-3 flex items-center justify-between">
                      <span className="text-sm font-semibold">Custo Total de Produção</span>
                      <span className="text-sm font-bold">{formatCurrency(selectedProduct.total_cost)}</span>
                    </div>
                    <div className="rounded-lg bg-secondary/50 p-3 flex items-center justify-between">
                      <span className="text-sm font-semibold">Lucro Bruto por Unidade</span>
                      <span className={`text-sm font-bold ${getMarginColor(selectedProduct.margin_pct)}`}>
                        {formatCurrency(selectedProduct.margin)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RelatorioCustos;
